import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

/**
 * Navigation component with desktop and mobile responsive views
 * Implements hamburger menu for mobile (Requirements 4.2, 4.6, 8.6)
 */
const Navigation = ({ currentPath }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation(['navigation', 'common']);
  const localizedPath = useLocalizedPath();

  // Navigation links configuration
  const navLinks = [
    { path: '/', label: t('menu.home') },
    { path: '/news', label: t('menu.news') },
    { path: '/activities', label: t('menu.activities') },
    { path: '/conferences', label: t('menu.conferences') },
    { path: '/awards', label: t('menu.awards') },
    { path: '/about', label: t('menu.about') },
  ];

  // Check if a link is active
  const isActive = (path) => {
    // Handle undefined or null currentPath
    if (!currentPath) {
      return false;
    }
    
    if (path === '/') {
      return currentPath === '/';
    }
    return currentPath.startsWith(path);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when a link is clicked
  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white border-t border-gray-200">
      <div className="container mx-auto px-4">
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={localizedPath(link.path)}
              className={`px-4 py-4 text-sm font-medium transition-colors relative ${
                isActive(link.path)
                  ? 'text-primary-600'
                  : 'text-gray-700 hover:text-primary-600'
              }`}
            >
              {link.label}
              {isActive(link.path) && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"></span>
              )}
            </Link>
          ))}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          {/* Hamburger button */}
          <button
            onClick={toggleMobileMenu}
            className="flex items-center justify-between w-full py-3 text-gray-700 hover:text-primary-600"
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="font-medium">{t('common:actions.menu')}</span>
            <div className="w-6 h-6 flex flex-col justify-center space-y-1.5">
              <span
                className={`block h-0.5 bg-current transition-transform duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              ></span>
              <span
                className={`block h-0.5 bg-current transition-opacity duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}
              ></span>
              <span
                className={`block h-0.5 bg-current transition-transform duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              ></span>
            </div>
          </button>

          {/* Mobile menu dropdown */}
          <div
            className={`overflow-hidden transition-all duration-300 ${
              isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="py-2 space-y-1 border-t border-gray-200">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={localizedPath(link.path)}
                  onClick={handleLinkClick}
                  className={`block px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
